import { useRef } from "react";

// Cache to store stable URLs by their base path (without query params)
const urlCache = new Map<string, string>();

function getBaseUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url;
  }
}

/**
 * Returns a stable URL that doesn't change on each render.
 * Convex storage URLs include signed tokens that change on each query,
 * causing browsers to reload images. This hook caches URLs by their
 * base path (without query params) to prevent unnecessary reloads.
 */
export function useStableUrl(url: string | null | undefined): string | null {
  const lastUrl = useRef<string | null>(null);

  if (!url) return null;

  const baseUrl = getBaseUrl(url);
  const cachedUrl = urlCache.get(baseUrl);

  if (cachedUrl) {
    // Return cached URL if base matches
    lastUrl.current = cachedUrl;
    return cachedUrl;
  }

  // Store new URL in cache
  urlCache.set(baseUrl, url);
  lastUrl.current = url;
  return url;
}

function stabilizeUrl(url: string): string {
  const baseUrl = getBaseUrl(url);
  const cached = urlCache.get(baseUrl);
  if (cached) return cached;
  urlCache.set(baseUrl, url);
  return url;
}

/**
 * Returns stable URLs for an array of items with url properties.
 * Also stabilizes referenceUrl and referenceUrls if present.
 */
export function useStableUrls<T extends { url?: string | null; referenceUrl?: string | null; referenceUrls?: string[] }>(
  items: T[] | undefined
): T[] | undefined {
  if (!items) return undefined;

  return items.map((item) => {
    let changed = false;
    let stableUrl = item.url;
    let stableRefUrl = item.referenceUrl;
    let stableRefUrls = item.referenceUrls;

    if (item.url) {
      const s = stabilizeUrl(item.url);
      if (s !== item.url) { stableUrl = s; changed = true; }
    }

    if (item.referenceUrl) {
      const s = stabilizeUrl(item.referenceUrl);
      if (s !== item.referenceUrl) { stableRefUrl = s; changed = true; }
    }

    if (item.referenceUrls && item.referenceUrls.length > 0) {
      const stabilized = item.referenceUrls.map(stabilizeUrl);
      if (stabilized.some((s, i) => s !== item.referenceUrls![i])) {
        stableRefUrls = stabilized;
        changed = true;
      }
    }

    if (!changed) return item;
    return { ...item, url: stableUrl, referenceUrl: stableRefUrl, referenceUrls: stableRefUrls };
  });
}
